// Bottom-sheet "What's new" inbox. Placeholder for now — when Proud wants
// to push an announcement (release note, scheduled downtime, feature
// spotlight), we'll populate this list. The UI is in place so the bell
// button in the AppHeader has somewhere to go.

import { useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { C } from '@/styles/tokens';

interface NotificationsSheetProps {
  onClose: () => void;
}

export function NotificationsSheet({ onClose }: NotificationsSheetProps) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal aria-label="What's new" className="fixed inset-0 z-50 flex flex-col">
      <div
        className="absolute inset-0 anim-backdrop-in"
        style={{ background: 'rgba(26,22,20,0.45)' }}
        aria-hidden
        onClick={onClose}
      />
      <div
        className="relative mt-auto flex flex-col rounded-t-[28px] overflow-hidden anim-sheet-up"
        style={{ background: C.cream, boxShadow: '0 -10px 30px rgba(26,22,20,0.22)', maxHeight: '85vh' }}
      >
        <span
          aria-hidden
          className="absolute top-2 left-1/2 w-10 h-1 rounded-full"
          style={{ transform: 'translateX(-50%)', background: 'rgba(26,22,20,0.18)' }}
        />

        <header
          className="pt-6 pb-3 px-5 flex items-center justify-between"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
        >
          <div className="ff-display italic text-2xl flex items-baseline gap-2" style={{ color: C.ink }}>
            What&apos;s new
            <span className="ff-zh-serif not-italic text-base opacity-50">· 最新消息</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: 'white' }}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="px-5 py-10 flex flex-col items-center text-center safe-bottom">
          <span
            aria-hidden
            className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
            style={{ background: C.blush, color: C.vermillionDeep }}
          >
            <Bell size={22} />
          </span>
          <div className="ff-display italic text-xl mb-1" style={{ color: C.ink }}>
            No updates yet
          </div>
          <div className="ff-zh-serif text-sm mb-3" style={{ color: C.inkLight }}>
            还没有消息
          </div>
          <p className="ff-body text-[12px] max-w-[260px] leading-relaxed" style={{ color: C.inkLight }}>
            New features, tips and announcements will appear here.
          </p>
        </div>
      </div>
    </div>
  );
}
